import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend
} from "recharts";

import {
    FaBolt,
    FaCloud,
    FaGaugeHigh,
    FaDroplet,
    FaTrash,
    FaBuilding
} from "react-icons/fa6";

import apiClient from "../../shared/API/apiClient";

import {
    useReportingYear
} from "../../context/ReportingYearContext";

const COLORS = [
    "#ef4444",
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#8b5cf6"
];
const SITE_COLORS = [
    "#2563eb",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#14b8a6",
    "#ec4899"
];
const ICONS = {
    energy: FaBolt,
    emissions: FaCloud,
    intensity: FaGaugeHigh,
    water: FaDroplet,
    waste: FaTrash
};

const formatNumber = value => {

    if(
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return Number(value).toLocaleString(
        "en-IN",
        {
            maximumFractionDigits: 2
        }
    );
};

const ExecutiveDashboardPage = () => {

    const { reportingYear, reportingYearId } = useReportingYear();
    const [reportingYearInfo, setReportingYearInfo] =useState(null);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [legalEntities, setLegalEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState("");
    const [selectedTrend,setSelectedTrend]=useState("energy");
    const [selectedBenchmark, setSelectedBenchmark] = useState("energy");
    const navigate = useNavigate();
    // =====================================
    // ENTITY
    // =====================================
useEffect(() => {

   const loadReportingYear = async () => {

    try {

        const res =
            await apiClient.post(
                "/api/reportingyear"
            );

        if (res.data.code === 200) {

            const selected =
                (res.data.data || [])
                    .find(
                        y =>
                            Number(y.id) ===
                            Number(reportingYearId)
                    );

            setReportingYearInfo(
                selected || null
            );
        }

    } catch (e) {

        console.error(
            "Reporting year API error:",
            e
        );
    }
};

    loadReportingYear();

}, [reportingYearId]);
    useEffect(() => {

        const load = async () => {

            if(!reportingYearId) {
                return;
            }

            try {

                const res =
                    await apiClient.get(
                        "/api/legal-entities-by-year",
                        {
                            params: {
                                reporting_year_id:
                                    reportingYearId
                            }
                        }
                    );

                setLegalEntities(
                    res.data.data || []
                );

            } catch {

                setLegalEntities([]);
            }
        };

        load();

    }, [reportingYearId]);

    // =====================================
    // FILTER
    // =====================================

    const [filters, setFilters] =
        useState({
            filter_type: "THIS_YEAR",
            quarter: "",
            month: "",
            from_date: "",
            to_date: ""
        });

      const handleFilterChange = (field, value) => {

    const updated = {
        ...filters,
        [field]: value
    };

    if(field === "filter_type") {

        updated.quarter = "";
        updated.month = "";

        if(value === "CUSTOM" && reportingYearInfo) {
            updated.from_date =
                reportingYearInfo.start_date;

            updated.to_date =
                reportingYearInfo.end_date;
        }

        if(value !== "CUSTOM") {
            updated.from_date = "";
            updated.to_date = "";
        }
    }

    setFilters(updated);
};

    // =====================================
    // LOAD
    // =====================================

    const loadReport = async () => {

        if(
            !reportingYearId ||
            !selectedEntity
        ) {
            return;
        }

        try {

            setLoading(true);

            const response =
            await apiClient.get(
                "/api/reports/executive-dashboard",
                {
                    params: {

                        reporting_year_id:reportingYearId,
                        legal_entity_id:selectedEntity
                    }
                }
            );

        setReport(response.data);

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        loadReport();

    }, [
        reportingYearId,
        selectedEntity
    ]);

    if(loading) {

        return (
            <div className="dash-loader-wrapper">
                <div className="dash-loader" />
            </div>
        );
    }
    return (

    <div className="dash-page">

        <div className="dash-page-title">
            Executive Dashboard
        </div>

        <div className="dash-page-subtitle">
             ESG Performance Overview & Executive Insights
        </div>

        {/* FILTER */}

        <div className="filter-card">
            <div className="filter-group">
                <label>Legal Entity</label>
                <select
                    className="filter-fields"
                    value={selectedEntity}
                    onChange={e => setSelectedEntity(e.target.value)}
                >
                    <option value="">Select</option>
                    {legalEntities.map(e => (
                        <option key={e.entity_id} value={e.entity_id}>
                            {e.entity_name}
                        </option>
                    ))}
                </select>
            </div>

           
        </div>
        {
                !report && (

                    <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity to view the Executive Dashboard.
          </div>
          
          
        
        </div>
                )
            }

        {
            report &&(() => {

                const {
                        kpis,
                        compliance,
                        health,
                        trends,
                        benchmarking,
                        alerts,
                        recommendations
                    } = report;

                return (

                    <>

                       <div className="reporting_year_line">
    <strong>{reportingYear}</strong>
    {selectedEntity &&
        ` | ${
            legalEntities.find(
                e => Number(e.entity_id) === Number(selectedEntity)
            )?.entity_name || ""
        }`
    }
</div>
                        {/* KPI */}

                        <div className="dash-stat-grid" style={{
                                    gridTemplateColumns:
                                        `repeat(${kpis.length},1fr)`
                                }}>

                            {
                                kpis.map(kpi => {
                                    const Icon = ICONS[kpi.key];

                                    return (
                                        <div key={kpi.key} className="dash-stat-card">

                                            <div className="dash-stat-header">
                                            <span className="metric-card__icon">
                                                <Icon />
                                                </span>
                                                <div className="dash-stat-label">{kpi.title}</div>
                                            </div>

                                            <div className="dash-stat-value">{kpi.value}</div>

                                            <div className="dash-stat-sub">{kpi.unit}</div>

                                        </div>
                                    );
                                })
                            }

                        </div>
                        
<div className="chart-row">
   <div className="chart-card">
    <h4>ESG Health Score</h4>

    <div className="health-card">
        <div
            className="health-circle"
            style={{ borderColor: health.color }}
        >
            <div className="health-score">{health.score}</div>
            <div className="health-grade">Grade {health.grade}</div>
        </div>

        <div className="health-badge">{health.status}</div>

        <div className="score-row">
            <div className="score-header">
                <span>Compliance Score</span>
                <strong>{health.breakdown.compliance_score} / 80</strong>
            </div>

            <div className="score-bar">
                <div
                    className="score-fill blue"
                    style={{
                        width: `${health.breakdown.compliance_score / 80 * 100}%`
                    }}
                />
            </div>
        </div>

        <div className="score-row">
            <div className="score-header">
                <span>Environmental Score</span>
                <strong>{health.breakdown.environment_score} / 20</strong>
            </div>

            <div className="score-bar">
                <div
                    className="score-fill green"
                    style={{
                        width: `${health.breakdown.environment_score / 20 * 100}%`
                    }}
                />
            </div>
        </div>
    </div>
</div>  
<div className="chart-card">
    <h4>Compliance Overview</h4>

    <div className="compliance-card">
        <div className="progress-text">
            Overall Completion
            <strong>{compliance.completion_percentage}%</strong>
        </div>

        <div className="compliance-progress">
            <div
                className="compliance-progress-fill"
                style={{ width: `${compliance.completion_percentage}%` }}
            />
        </div>

        <div className="summary-grid">
            <div className="summary-card">
                <div className="summary-number">{compliance.completed}</div>
                <div className="summary-title">Completed</div>
            </div>

            <div className="summary-card">
                <div className="summary-number">{compliance.in_progress}</div>
                <div className="summary-title">In Review</div>
            </div>

            <div className="summary-card">
                <div className="summary-number">{compliance.pending}</div>
                <div className="summary-title">Pending</div>
            </div>
        </div>

        <div className="principle-grid">
            {compliance.principles.map(p => (
                <div
                    key={p.principle_id}
                    className={`principle-card status-${p.status}`}
                >
                    <div className="principle-header">
                        <div className="principle-title">{p.principle}</div>
                    </div>

                    <div className="principle-status">
                        {p.status.replaceAll("_", " ")}
                    </div>
                </div>
            ))}
        </div>
    </div>
</div>
</div>
<div className="chart-card">
    <div className="chart-header">
        <h4>Monthly ESG Trends</h4>

        <div className="trend-tabs">
            <button
                className={selectedTrend === "energy" ? "active" : ""}
                onClick={() => setSelectedTrend("energy")}
            >
                Energy
            </button>

            <button
                className={selectedTrend === "emissions" ? "active" : ""}
                onClick={() => setSelectedTrend("emissions")}
            >
                Emissions
            </button>

            <button
                className={selectedTrend === "water" ? "active" : ""}
                onClick={() => setSelectedTrend("water")}
            >
                Water
            </button>

            <button
                className={selectedTrend === "waste" ? "active" : ""}
                onClick={() => setSelectedTrend("waste")}
            >
                Waste
            </button>
        </div>
    </div>

    <ResponsiveContainer width="100%" height={360}>
        <LineChart data={report.trends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Line
                type="monotone"
                dataKey={selectedTrend}
                stroke="#2563eb"
                strokeWidth={3}
                dot={{ r: 4 }}
            />
        </LineChart>
    </ResponsiveContainer>
</div>
{report?.benchmarking?.entities?.length > 0 && (
    <div className="chart-card chart-row">
    <div className="chart-header">
        <h4>Benchmarking</h4>

        <select
            className="filter-fields"
            value={selectedBenchmark}
            onChange={e => setSelectedBenchmark(e.target.value)}
        >
            <option value="energy">Energy</option>
            <option value="emissions">Emissions</option>
            <option value="water">Water</option>
            <option value="waste">Waste</option>
        </select>
    </div>

    <table className="table">
        <thead>
            <tr>
                <th>Rank</th>
                <th>Legal Entity</th>
                <th>Value</th>
            </tr>
        </thead>

        <tbody>
            {(
                selectedBenchmark === "energy"
                    ? report.benchmarking.energy_ranking
                    : selectedTrend === "emissions"
                    ? report.benchmarking.emissions_ranking
                    : selectedTrend === "water"
                    ? report.benchmarking.water_ranking
                    : report.benchmarking.waste_ranking
            ).map((item, index) => (
                <tr
                    key={item.legal_entity_id}
                    className={
                        Number(item.legal_entity_id) === Number(selectedEntity)
                            ? "benchmark-selected"
                            : ""
                    }
                >
                    <td>#{index + 1}</td>
                    <td>{item.entity_name}</td>
                    <td>{formatNumber(item.value)} {item.unit}</td>
                </tr>
            ))}
        </tbody>
    </table>
</div>

)}
{report?.recommendations?.length > 0 && (
    <div className="chart-card chart-row">
        <h4>Recommended Actions</h4>

        <div className="recommendation-grid">
            {report.recommendations.map((item, index) => (
                <div
    key={index}
    className={`recommendation-card ${item.priority}`}
>
    <div className={`priority-badge ${item.priority}`}>
        {item.priority.toUpperCase()} PRIORITY
    </div>

    <div className="recommendation-title">{item.title}</div>

    <div className="recommendation-message">
        {item.message}
    </div>
    {item.route && (
    <button
        className="org-btn-save"
        onClick={() =>
            navigate(item.route, {
                state: {
                    reportingYearId,
                    legalEntityId: selectedEntity
                }
            })
        }
    >
        {item.action}
    </button>
)}
</div>
            ))}
        </div>
    </div>
)}
{report?.alerts?.length > 0 && (
    <div className="chart-card">
        <h4>Alerts</h4>

        <div className="alerts-list">
            {report.alerts.map((alert, index) => (
                <div
                    key={index}
                    className={`alert-card ${alert.severity}`}
                >
                    <div className="alert-left">
                        <div className={`alert-dot ${alert.severity}`} />

                        <div>
                        <div className="alert-category">
                            {alert.category}
                        </div>
                            <div className="alert-title">{alert.title}</div>
                            <div className="alert-message">{alert.message}</div>
                        </div>
                    </div>

                    {alert.action && (
                        <button className="org-btn-save" onClick={() =>
        navigate(alert.route, {
            state: {
                reportingYearId,
                legalEntityId: selectedEntity
            }
        })
    }>
                            {alert.action}
                        </button>
                    )}
                </div>
            ))}
        </div>
    </div>
)}
                    </>
                );
            })()
        }

                    </div>
            );
            };

export default ExecutiveDashboardPage;